import { queryOptions } from "@tanstack/react-query";
import {
	type FormDefinitionRequestOptions,
	formDefinitionService,
} from "./form-definition.service";
import type {
	FormDefinition,
	FormDefinitionQueryData,
	FormDefinitionScopeQuery,
	FormDefinitionsListParams,
} from "./form-definition-core.types";
import { parseLayoutJsonWire } from "./layout-json-envelope.schema";
import { resolveFormRulesFromFormDefinition } from "./resolve-form-definition-form-rules.util";

// ==================== QUERY KEYS ====================

export const formDefinitionKeys = {
	all: ["form-definition"] as const,
	count: (params?: FormDefinitionsListParams) =>
		[...formDefinitionKeys.all, "count", params] as const,
	page: (start: number, count: number, params?: FormDefinitionsListParams) =>
		[...formDefinitionKeys.all, "page", start, count, params] as const,
	detail: (id: string) => [...formDefinitionKeys.all, "detail", id] as const,
	current: (params?: FormDefinitionScopeQuery, organizationId?: string) =>
		[
			...formDefinitionKeys.all,
			"current",
			params,
			organizationId ?? "",
		] as const,
	active: (params?: FormDefinitionScopeQuery) =>
		[...formDefinitionKeys.all, "active", params] as const,
} as const;

const FORM_DEF_STALE_MS = 5 * 60 * 1000;
const FORM_DEF_GC_MS = 30 * 60 * 1000;

export function mapRowToQueryData(
	item: FormDefinition,
): FormDefinitionQueryData {
	const wireLayoutJson = item.layoutJson ?? null;
	const parsed = parseLayoutJsonWire(wireLayoutJson);
	const layout = parsed?.envelope.layout ?? null;
	const formRules = resolveFormRulesFromFormDefinition(item);
	const { layoutJson: _wireLayout, formRules: _wireRules, ...rest } = item;
	return {
		...rest,
		layout,
		layoutEnvelope: parsed?.envelope ?? null,
		formRules,
		wireLayoutJson,
		layoutJson: layout,
	};
}

// ==================== QUERY OPTIONS ====================

export const formDefinitionQueryOptions = {
	count: (params?: FormDefinitionsListParams) =>
		queryOptions({
			queryKey: formDefinitionKeys.count(params),
			queryFn: () => formDefinitionService.getFormDefinitionsCount(params),
			staleTime: FORM_DEF_STALE_MS,
			gcTime: FORM_DEF_GC_MS,
		}),

	page: (start: number, count: number, params?: FormDefinitionsListParams) =>
		queryOptions({
			queryKey: formDefinitionKeys.page(start, count, params),
			queryFn: () =>
				formDefinitionService.getFormDefinitionsPage(start, count, params),
			select: (data) =>
				data.map((item) => mapRowToQueryData(item as FormDefinition)),
			staleTime: FORM_DEF_STALE_MS,
			gcTime: FORM_DEF_GC_MS,
		}),

	detail: (id: string) =>
		queryOptions({
			queryKey: formDefinitionKeys.detail(id),
			queryFn: () => formDefinitionService.getFormDefinitionById(id),
			select: (data) => mapRowToQueryData(data as FormDefinition),
			staleTime: FORM_DEF_STALE_MS,
			gcTime: FORM_DEF_GC_MS,
			enabled: !!id && id.length > 0,
		}),

	current: (
		params?: FormDefinitionScopeQuery,
		requestOptions?: FormDefinitionRequestOptions,
	) =>
		queryOptions({
			queryKey: formDefinitionKeys.current(
				params,
				requestOptions?.organizationId,
			),
			queryFn: () =>
				formDefinitionService.getCurrentFormDefinition(params, requestOptions),
			select: (data): FormDefinitionQueryData | null =>
				data == null ? null : mapRowToQueryData(data),
			staleTime: FORM_DEF_STALE_MS,
			gcTime: FORM_DEF_GC_MS,
			retry: false,
		}),

	active: (params?: FormDefinitionScopeQuery) =>
		queryOptions({
			queryKey: formDefinitionKeys.active(params),
			queryFn: () => formDefinitionService.getActiveFormDefinition(params),
			select: (data): FormDefinitionQueryData | null =>
				data == null ? null : mapRowToQueryData(data),
			staleTime: FORM_DEF_STALE_MS,
			gcTime: FORM_DEF_GC_MS,
		}),
} as const;
